import { TextField } from '@shinetools/lumen-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { countriesWithCompanySearchEnabled } from '@features/companies/elements/CompanySearchField/constants/countriesWithCompanySearchEnabled'
import { computeVatNumber } from '@features/companies/utils/computeVatNumber'
import { isFrenchCompany } from '@features/companies/utils/isFrenchCompany'
import { Company, FrenchCompany } from '@modules-deprecated/app/companies/types'
import { getCompanyName } from '@modules-deprecated/app/companies/utils/getCompanyName'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { DesignSystem } from '../../../../../../../../../../../../../../enums/designSystem'
import { useFormContext } from '../../../../../../../../../../../../../../hooks/useForm'
import type { SupplierFormSchema, SupplierFormValues } from '../../../../../../types/supplierForm'
import { CompanySearchDropdown } from '../../../CompanySearchDropdown'
import { CompanyRegistrySearchField } from './elements/CompanyRegistrySearchField'

const COMPANY_FIELDS = [
  'cityText',
  'countryId',
  'name',
  'phone',
  'registrationNo',
  'stateText',
  'street',
  'vatNo',
  'zipcodeText',
] as const

type CompanyFields = Partial<Pick<SupplierFormValues, (typeof COMPANY_FIELDS)[number]>>

const getCountryIdFromCompany = (
  company: Partial<Company> | Partial<FrenchCompany>,
  organizationCountryId?: string,
): string | undefined => {
  if ('country' in company && typeof company.country === 'string') {
    return company.country
  }

  // Danish CVR results have no country, and a selected company is a Danish company.
  if (organizationCountryId === 'DK') {
    return organizationCountryId
  }

  return undefined
}

// Registry results carry the company's current tax ID and address. Without these, a search
// selection would attach the new company's name to whichever supplier was previously stored.
const getFieldsFromCompany = (
  company: Partial<Company> | Partial<FrenchCompany>,
  organizationCountryId?: string,
): CompanyFields => {
  const countryId = getCountryIdFromCompany(company, organizationCountryId)
  const name = getCompanyName(company) ?? company.id ?? ''

  if (isFrenchCompany(company)) {
    return {
      cityText: company.city ?? undefined,
      countryId,
      name,
      street: company.street ?? undefined,
      vatNo: computeVatNumber('FR', company.siret) ?? undefined,
      zipcodeText: company.zipcode ?? undefined,
    }
  }

  return {
    cityText: company.city,
    countryId,
    name,
    phone: 'phone' in company ? (company.phone ?? undefined) : undefined,
    registrationNo: company.id,
    stateText: company.state,
    street: company.street,
    vatNo: company.vatNo ?? undefined,
    zipcodeText: company.zipcode,
  }
}

export const CompanyName = () => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { FormItem, setValue } = useFormContext<SupplierFormSchema, DesignSystem.Lumen>({
    designSystem: DesignSystem.Lumen,
  })
  const organizationCountryId = organization?.countryId
  const isDanishOrganization = organizationCountryId === 'DK'
  const isCompanyRegistrySearchEnabled =
    !!organizationCountryId && countriesWithCompanySearchEnabled.includes(organizationCountryId)

  // A registry result leaves the fields it has no value for as they are.
  const handleCompanySelect = useCallback(
    (company: Partial<Company> | Partial<FrenchCompany>) => {
      const companyFields = getFieldsFromCompany(company, organizationCountryId)

      for (const field of COMPANY_FIELDS) {
        const value = companyFields[field]

        if (value !== undefined) {
          setValue(field, value, { shouldDirty: true, shouldValidate: true })
        }
      }
    },
    [organizationCountryId, setValue],
  )

  if (isDanishOrganization) {
    return (
      <FormItem
        name="name"
        render={({ error, field }) => (
          <CompanySearchDropdown
            error={error}
            name={field.value}
            onNameChange={field.onChange}
            onSelect={handleCompanySelect}
          />
        )}
      />
    )
  }

  if (isCompanyRegistrySearchEnabled) {
    return <CompanyRegistrySearchField onSelectCompany={handleCompanySelect} />
  }

  return (
    <FormItem
      name="name"
      render={({ error, field }) => (
        <TextField
          {...field}
          error={error}
          label={t('suppliers.detail.form.company_name.label')}
          placeholder={t('companies.company_search_field.placeholder')}
          size="small"
        />
      )}
    />
  )
}
